import { PrismicRichText } from '@prismicio/react';
import Image from 'next/image';
import PlyrJS from 'plyr';
import { useEffect, useRef, useState } from 'react';
import ContentContainer from 'remote-com-shared/components/ContentContainer';
import { fieldHasContent, transformProperties } from 'remote-com-shared/domains/prismic/helpers';
import { fluidProp } from 'remote-com-shared/style/helpers';
import styled from 'styled-components';

import { PlayIcon } from '../assets/PlayIcon';
import VideoThumbnail from '../assets/video-thumbnail.png';
import { ReadMore } from '../components/ReadMore';

import { components } from './GlobalPayrollSlice';

import {
  ButtonCloseStyled,
  Content,
  StyledVideo,
  VideoContainer,
  VideoOverlay,
} from '@/src/domains/homepage/styles';

import 'plyr/dist/plyr.css';

export const BestPracticesContainer = styled(ContentContainer)`
  ${fluidProp('padding-top', '100px', '150px')};
`;

const Label = styled.div`
  margin-bottom: 10px;
  text-transform: uppercase;
  color: var(--colors-irisBlue);
  ${({ theme }) => theme.typography.bodyXS};
`;

export const Description = styled.div`
  ${({ theme }) => theme.typography.body};
  color: var(--colors-lynch);

  & > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }
`;

const BestPracticeBlock = styled.div`
  margin-bottom: 50px;
`;

export const Title = styled.div`
  ${({ theme }) => theme.typography.scale4};
  ${fluidProp('font-size', '24px', '36px')};
  strong {
    color: var(--colors-irisBlue);
    font-weight: 600;
  }

  & > * {
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
    margin: inherit;
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  z-index: 1;
  inset: 0 0 0 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VideoAreaContainer = styled.div`
  position: relative;
  margin: 50px 0 75px;
  width: 100%;
  cursor: pointer;
`;

export const Plyr = () => {
  const plyrRef = useRef(null);

  useEffect(() => {
    if (!plyrRef.current) return;
    plyrRef.current.plyr = new PlyrJS('#player-js', {
      quality: {
        default: 720,
      },
    });

    plyrRef.current.plyr.source = {
      type: 'video',
      sources: [
        {
          src: '/images/RemoteExplainerFinalSmaller.mp4#t=0',
          type: 'video/mp4',
          size: 480,
        },
        {
          src: '/images/RemoteExplainerFinal.mp4#t=0',
          type: 'video/mp4',
          size: 720,
        },
      ],
    };

    plyrRef.current.plyr.play();
  }, [plyrRef]);

  return (
    <VideoContainer>
      <StyledVideo playsinline controls ref={plyrRef} id="player-js" />
    </VideoContainer>
  );
};

export function PlayVideoArea({ illustration }) {
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);

  function handleShowVideoDialog(event) {
    event.preventDefault();
    setIsVideoDialogOpen(true);
  }
  function handleDismissVideoDialog(event) {
    event.preventDefault();
    setIsVideoDialogOpen(false);
  }

  return (
    <>
      <VideoAreaContainer onClick={handleShowVideoDialog}>
        <Image
          src={illustration.src}
          alt={illustration.alt || ''}
          width={illustration.width}
          height={illustration.height}
          quality="100"
          layout="responsive"
        />
        <IconWrapper>
          <PlayIcon />
        </IconWrapper>
      </VideoAreaContainer>
      <VideoOverlay isOpen={isVideoDialogOpen} onDismiss={handleDismissVideoDialog}>
        <Content aria-label="Global payroll">
          <ButtonCloseStyled onClick={handleDismissVideoDialog} aria-label="Close dialog" />
          <Plyr />
        </Content>
      </VideoOverlay>
    </>
  );
}

export function BestPracticesSlice({ slice }) {
  const { primary, fields } = transformProperties(slice);
  const {
    bestPracticesLabel,
    bestPracticesReadMoreTitle,
    bestPracticesReadMoreImage,
    compensationLinkLabel,
    compensationLink,
    globalCompensationLabel,
    globalCompensationLink,
    taxesLinkLabel,
    taxesLink,
    videoEmbedTitle,
    additionalLinkLabel,
    additionalLink,
  } = primary;

  const readMoreLinks = [
    {
      label: compensationLinkLabel,
      link: compensationLink,
    },
    {
      label: globalCompensationLabel,
      link: globalCompensationLink,
    },
    {
      label: taxesLinkLabel,
      link: taxesLink,
    },
    {
      label: additionalLinkLabel,
      link: additionalLink,
    },
  ];

  return (
    <BestPracticesContainer id="best-practices">
      {bestPracticesLabel && <Label>{bestPracticesLabel}</Label>}
      {fields?.map(({ stepTitle, stepDescription }, index) => (
        <BestPracticeBlock key={index}>
          {fieldHasContent(stepTitle) && (
            <Title>
              <PrismicRichText field={stepTitle} />
            </Title>
          )}
          {fieldHasContent(stepDescription) && (
            <Description>
              <PrismicRichText field={stepDescription} components={components} />
            </Description>
          )}
        </BestPracticeBlock>
      ))}
      <ReadMore
        title={bestPracticesReadMoreTitle}
        image={bestPracticesReadMoreImage}
        links={readMoreLinks}
      />
      {videoEmbedTitle && (
        <Title>
          <PrismicRichText field={videoEmbedTitle} />
        </Title>
      )}
      <PlayVideoArea aria-label="Global Payroll" illustration={VideoThumbnail} />
    </BestPracticesContainer>
  );
}
